import { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';
import { io } from 'socket.io-client';
import { useLocale } from '../../../utils/LocaleContext';
import { canViewTab } from '../../../utils/rbac';
import { formatDateTime } from '../../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';
const SOCKET_URL = process.env.REACT_APP_BASE_SOCKET_URL || 'http://localhost:8080';

const STATUS_CONFIG = {
  reported:      { labelKey: 'screens.claimsScreen.statusReported',      bg: '#dbeafe', color: '#1d4ed8' },
  new:           { labelKey: 'screens.claimsScreen.statusReported',      bg: '#dbeafe', color: '#1d4ed8' }, // alias for backward compat
  open:          { labelKey: 'screens.claimsScreen.statusOpen',          bg: '#dcfce7', color: '#15803d' },
  review:        { labelKey: 'screens.claimsScreen.statusReview',        bg: '#fef3c7', color: '#92400e' },
  investigation: { labelKey: 'screens.claimsScreen.statusInvestigation', bg: '#e0e7ff', color: '#4338ca' },
  evaluation:    { labelKey: 'screens.claimsScreen.statusEvaluation',    bg: '#c7d2fe', color: '#4f46e5' },
  negotiation:   { labelKey: 'screens.claimsScreen.statusNegotiation',   bg: '#fce7f3', color: '#9d174d' },
  approved:      { labelKey: 'screens.claimsScreen.statusApproved',      bg: '#d1fae5', color: '#065f46' },
  denied:        { labelKey: 'screens.claimsScreen.statusDenied',        bg: '#fee2e2', color: '#991b1b' },
  closed:        { labelKey: 'screens.claimsScreen.statusClosed',        bg: '#f1f5f9', color: '#64748b' },
  reopened:      { labelKey: 'screens.claimsScreen.statusReopened',      bg: '#ede9fe', color: '#7c3aed' },
};

const statusLabel = (status, t) => {
  const cfg = STATUS_CONFIG[status];
  return cfg ? t(cfg.labelKey) : status;
};

const SEVERITY_COLORS = { critical: '#dc2626', high: '#ea580c', medium: '#d97706', low: '#16a34a' };

const S = {
  burgundy: '#800020',
  burgundy2: '#722f37',
  textPrimary: '#1e293b',
  textSecondary: '#475569',
  textMuted: '#64748b',
  borderLight: '#e2e8f0',
  bgCard: '#f8fafc',
  bgHover: '#f1f5f9',
};

// Map API claim document → display format.
// Preserves the raw fields under their original names so that modals which
// edit the raw doc (EditClaimModal) can read from the same object. Prior to
// the T-10.5 fix the display object renamed reserveAmount→reserve and
// lossDescription→description; the edit modal read the raw names and
// silently defaulted to 0 / empty, then wrote those defaults back on save.
function toClaimDetail(c, t) {
  const assignedName = c.assignedTo?.name || null;
  const assignedInitials = assignedName
    ? assignedName.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase()
    : null;
  const lobLabels = {
    professional_liability: t('screens.claimsScreen.lobProfessionalLiability'),
    daily_sickness: t('screens.claimsScreen.lobDailySickness'),
    legal_protection: t('screens.claimsScreen.lobLegalProtection'),
    property: t('screens.claimsScreen.lobProperty'),
    workers_comp: t('screens.claimsScreen.lobWorkersComp'),
    auto: t('screens.claimsScreen.lobAuto'),
    health: t('screens.claimsScreen.lobHealth'),
    casualty: t('screens.claimsScreen.lobCasualty'),
    commercial: t('screens.claimsScreen.lobCommercial'),
  };
  return {
    // Raw fields preserved for edit modals + audit tabs.
    ...c,
    // Display-friendly overrides used by Header + Table.
    id: c.claimNumber || c._id,
    claimantDisplay: c.claimant?.name || '—',
    company: c.claimant?.company || '—',
    lob: lobLabels[c.lineOfBusiness] || c.lineOfBusiness || '—',
    status: c.status || 'new',
    priority: c.priority || 'medium',
    assigned: assignedName,
    assignedInitials,
    // NOT overridden with a display dash: `...c` above keeps the raw fields for
    // the edit modals, and this key IS a raw field. A '—' here would seed any
    // form that reads claim.policyNumber. The Header renders the dash.
    lossDate: c.lossDate || new Date().toISOString(),
    // "Filed" is a BUSINESS date — when the case was reported — and the label
    // says so in all three locales ("Filed" / "Déclaré le" / "Gemeldet am").
    // It was reading `createdAt`, which is when Regure ingested the record.
    // For anything loaded through the historical import the two are far apart,
    // and the header then asserted a chronology that contradicted itself:
    // CL-2026-07005 read "Filed 14/08/2026" above a timeline showing it
    // reported on 02/06 and closed on 03/06 — filed two months after it
    // closed. Nothing was wrong with the data; the field was mislabelled.
    //
    // createdAt is kept, separately and under its own name, rather than
    // overwritten: when the record entered the system is a real fact and the
    // one thing that must never be back-dated to fake a history.
    filed: c.reportedDate || c.createdAt || new Date().toISOString(),
    ingestedAt: c.createdAt || null,
    reserve: c.reserveAmount || c.currentReserve || 0,
    paidAmount: c.paidAmount || 0,
    description: c.lossDescription || c.description || '',
    _id: c._id,
  };
}

const useClaimDetailFunctions = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('timeline');
  const [claim, setClaim] = useState(null);
  const [claimLoading, setClaimLoading] = useState(true);
  const [claimError, setClaimError] = useState(null);
  const [notes, setNotes] = useState([]);
  const [reggieOpen, setReggieOpen] = useState(false);
  const [riskSignals, setRiskSignals] = useState([]);
  const [showSignals, setShowSignals] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [timelineEvents, setTimelineEvents] = useState([]);

  // ── RBAC ──────────────────────────────────────────────────
  const loggedinUser = useSelector(state => state.loggedinUser);
  const userData = loggedinUser?.userInfo?.data?.data;
  const userRole = userData?.role || 'adjuster';

  // Locale reactivity
  useLocale();

  // Build tab list, filtered by role
  const ALL_TABS = [
    { id: 'timeline', label: t('screens.claimsScreen.detail.tabTimeline') },
    { id: 'documents', label: t('screens.claimsScreen.detail.tabDocuments') },
    { id: 'notes', label: t('screens.claimsScreen.detail.tabNotes') },
    { id: 'financials', label: t('screens.claimsScreen.detail.tabFinancials') },
    { id: 'communications', label: t('screens.claimsScreen.detail.tabCommunications') },
    { id: 'audit', label: t('screens.claimsScreen.detail.tabAudit') },
  ];
  const tabs = ALL_TABS.filter(t => canViewTab(userRole, t.id));

  // Keep activeTab valid when tabs change
  useEffect(() => {
    if (!tabs.find(t => t.id === activeTab)) {
      setActiveTab(tabs[0]?.id || 'timeline');
    }
  }, [userRole]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset per-claim state whenever the URL id changes. Without this the
  // component reused the previously loaded claim across in-place route
  // transitions (/claims/A → /claims/B), which briefly rendered the
  // stale A detail underneath the fresh B fetch — causing T-10.5 where
  // scrolling revealed a "Claim not found" banner underneath a loaded
  // detail card.
  useEffect(() => {
    setClaim(null);
    setClaimError(null);
    setNotes([]);
    setTimelineEvents([]);
    setActiveTab(prev => prev || 'timeline');
  }, [id]);

  const fetchClaim = useCallback(async () => {
    setClaimLoading(true);
    setClaimError(null);
    try {
      const resp = await axios.get(`${BASE_URL}/claims/${id}`, { withCredentials: true });
      if (resp.data?.data) {
        const d = resp.data.data;
        setClaim(toClaimDetail(d, t));
        setNotes(d.notes || []);
        // Build timeline from statusHistory + notes
        // Two bugs lived in this block, and together they made the case
        // timeline unreadable.
        //
        // 1. `time` was formatted with toLocaleString(), which follows the
        //    BROWSER's locale and timezone rather than the tenant's. Every
        //    other date on the platform goes through formatDateTime for
        //    exactly this reason — a timestamp that reads differently for two
        //    colleagues in different countries is not a record of anything.
        //
        // 2. The sort then parsed that FORMATTED STRING back into a Date.
        //    `new Date('25/05/2026, 01:58:56')` is Invalid Date, so getTime()
        //    returned NaN, `|| 0` turned every key into 0, and the comparator
        //    answered 0 for every pair. The timeline was not sorted at all —
        //    it rendered in insertion order, every status event followed by
        //    every note. That is what put "closed" above "reported" on
        //    CL-2026-06992 and made the case look as though it had closed
        //    before it was reported.
        //
        // Keep the instant for ordering, derive the display string from it.
        // `ts` is what sorts; `time` is only ever read by a human.
        const toEventTime = (raw) => {
          if (!raw) return { ts: null, time: '—' };
          const d2 = new Date(raw);
          if (Number.isNaN(d2.getTime())) return { ts: null, time: '—' };
          return { ts: d2.getTime(), time: formatDateTime(raw) };
        };

        const statusEvents = (d.statusHistory || []).map((sh, i) => ({
          id: sh._id || `sh-${i}`,
          type: 'status',
          user: sh.changedBy?.name || sh.changedByName || t('screens.claimsScreen.detail.actorSystem'),
          action: t('screens.claimsScreen.detail.actionChangedStatusTo'),
          detail: statusLabel(sh.status, t),
          ...toEventTime(sh.timestamp),
          note: sh.reason || '',
        }));
        const noteEvents = (d.notes || []).map((n, i) => ({
          id: n._id || `n-${i}`,
          type: 'note',
          user: n.author?.name || n.authorName || t('screens.claimsScreen.detail.actionUnknown'),
          action: n.internal ? t('screens.claimsScreen.detail.actionAddedInternalNote') : t('screens.claimsScreen.detail.actionAddedNote'),
          detail: '',
          ...toEventTime(n.createdAt),
          note: n.text || '',
        }));
        // Newest first. Events with no usable timestamp sort last rather than
        // to the top — an undated event must not claim to be the most recent
        // thing that happened.
        const combined = [...statusEvents, ...noteEvents].sort((a, b) => {
          if (a.ts == null && b.ts == null) return 0;
          if (a.ts == null) return 1;
          if (b.ts == null) return -1;
          return b.ts - a.ts;
        });
        setTimelineEvents(combined);
      }
    } catch (e) {
      setClaimError(e.response?.data?.message || t('screens.claimsScreen.detail.loadFailed', { defaultValue: 'Failed to load claim' }));
    } finally {
      setClaimLoading(false);
    }
  }, [id]);

  const fetchSignals = useCallback(async () => {
    try {
      const resp = await axios.get(`${BASE_URL}/claims/${id}/risk-signals`, { withCredentials: true });
      if (resp.data?.data?.signals) {
        setRiskSignals(resp.data.data.signals.filter(s => s.status === 'active'));
      }
    } catch {
      /* silent */
    }
  }, [id]);

  useEffect(() => {
    fetchClaim();
    fetchSignals();
  }, [fetchClaim, fetchSignals]);

  // ── Socket.IO — live claim room ──────────────────────────
  const socketRef = useRef(null);
  useEffect(() => {
    if (!id) return;
    const socket = io(SOCKET_URL, { transports: ['websocket', 'polling'], reconnectionAttempts: 5 });
    socketRef.current = socket;
    socket.on('connect', () => socket.emit('join:claim', id));

    socket.on(`claim:${id}:updated`, data => {
      if (data.type === 'status') {
        toast.success(t('screens.claimsScreen.detail.toastStatusChanged', { from: data.from, to: data.to }), { duration: 4000 });
        setClaim(prev => (prev ? { ...prev, status: data.to } : prev));
        // Prepend a timeline event
        setTimelineEvents(prev => [
          {
            id: Date.now(),
            type: 'status',
            user: data.actor?.name || t('screens.claimsScreen.detail.actorSystem'),
            initials: 'SY',
            action: t('screens.claimsScreen.detail.actionChangedStatusTo'),
            detail: statusLabel(data.to, t),
            // Live socket event: it just happened, so it carries `now` as its
            // sort key and a friendly label as its text. Without `ts` a
            // subsequent re-sort would file it as undated and push it last.
            ts: Date.now(),
            time: t('screens.claimsScreen.detail.actorJustNow'),
            note: data.reason || '',
          },
          ...prev,
        ]);
        setActiveTab('timeline');
      } else if (data.type === 'assignment') {
        toast(t('screens.claimsScreen.detail.toastReassigned', { name: data.assignee?.name || t('screens.claimsScreen.detail.toastReassignedFallback') }), { icon: '👤', duration: 4000 });
        fetchClaim();
      } else if (data.type === 'reserve') {
        toast(t('screens.claimsScreen.detail.toastReserveUpdated', { id: data.claimNumber || id }), { icon: '💰', duration: 4000 });
        fetchClaim();
      } else {
        fetchClaim();
      }
    });

    socket.on(`claim:${id}:note`, data => {
      if (!data.internal) {
        toast(t('screens.claimsScreen.detail.toastNewNote', { name: data.actor?.name || t('screens.claimsScreen.detail.toastColleague') }), { icon: '📝', duration: 4000 });
      }
    });

    socket.on(`claim:${id}:signal`, data => {
      toast.error(t('screens.claimsScreen.detail.toastNewSignal', { severity: data.severity || '', title: data.title || '' }), { duration: 5000 });
      setRiskSignals(prev => {
        const exists = prev.some(s => s._id === data._id || s._id === data.signalId);
        if (exists) return prev;
        return [{ _id: data.signalId || data._id, severity: data.severity, title: data.title, description: data.description }, ...prev];
      });
    });

    return () => {
      socket.emit('leave:claim', id);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [id, fetchClaim]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    S,
    history,
    activeTab,
    setActiveTab,
    claim,
    setClaim,
    claimLoading,
    claimError,
    notes,
    setNotes,
    reggieOpen,
    setReggieOpen,
    riskSignals,
    showSignals,
    setShowSignals,
    showStatusModal,
    setShowStatusModal,
    showAssignModal,
    setShowAssignModal,
    showEditModal,
    setShowEditModal,
    timelineEvents,
    fetchClaim,
    userData,
    userRole,
    tabs,
    id,
    SEVERITY_COLORS,
  };
};

export default useClaimDetailFunctions;
